import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import { stringify } from 'hub-http/helpers/params';
import I18n from 'I18n';
import * as OfferingType from 'marketplace-ui-common/offerings/constants/OfferingType';
import { ecosystemAppClient, ecosystemPublicClient } from 'marketplace-ui-common/clients/ecosystemClient';
import { TYPEAHEAD_SEARCH_RESULTS_LIMIT } from 'marketplace-ui-common/constants/search';
import AssetSearchBarResult from '../components/AssetSearchBarResult';
import { getModuleMarketplaceUrl, getTemplateMarketplaceUrl, getThemeMarketplaceUrl } from '../../utils/urls';
export const VIEW_ALL_THEMES = '__VIEW_ALL_THEMES__';
export const VIEW_ALL_MODULES = '__VIEW_ALL_MODULES__';
export function useAssetSearchBarQuery({
  appContext = 'app',
  isUngatedForUnifiedSearch = false,
  grouped = false,
  limit = TYPEAHEAD_SEARCH_RESULTS_LIMIT,
  offeringType = 'THEME'
} = {}) {
  const offeringTypeIsModule = offeringType === OfferingType.MODULE || Array.isArray(offeringType) && offeringType.length === 1 && offeringType.includes('MODULE');
  let searchBarGroupLabelMessage;
  let searchUrl;
  let VIEW_ALL_ASSETS;

  if (isUngatedForUnifiedSearch) {
    searchUrl = 'assets/search';
    VIEW_ALL_ASSETS = '__VIEW_ALL_ASSETS__';
    searchBarGroupLabelMessage = I18n.text('marketplace-ui-assets-core.assets.offeringsSearchBarGroupLabel');
  } else {
    searchUrl = offeringTypeIsModule ? 'modules/search' : 'themes/search';
    VIEW_ALL_ASSETS = offeringTypeIsModule ? VIEW_ALL_MODULES : VIEW_ALL_THEMES;
    searchBarGroupLabelMessage = offeringTypeIsModule ? I18n.text('marketplace-ui-assets-core.assets.moduleSearchBarGroupLabel') : I18n.text('marketplace-ui-assets-core.assets.themeSearchBarGroupLabel');
  }

  const getAssetDetailsUrl = useCallback((_offeringType, pathname) => {
    return _offeringType === 'MODULE' ? getModuleMarketplaceUrl(appContext, pathname) : getThemeMarketplaceUrl(appContext, pathname);
  }, [appContext]);
  const getViewAllUrl = useCallback(input => {
    const query = stringify({
      eco_search: input
    });

    if (isUngatedForUnifiedSearch) {
      return getTemplateMarketplaceUrl(appContext, `search?${query}`);
    }

    return offeringTypeIsModule ? getModuleMarketplaceUrl(appContext, `all?${query}`) : getThemeMarketplaceUrl(appContext, `all?${query}`);
  }, [appContext, isUngatedForUnifiedSearch, offeringTypeIsModule]);
  const getViewAllSlug = useCallback(input => {
    const query = stringify({
      eco_search: input
    });

    if (isUngatedForUnifiedSearch) {
      return `search?${query}`;
    }

    return offeringTypeIsModule ? `all?${query}` : `all?${query}`;
  }, [isUngatedForUnifiedSearch, offeringTypeIsModule]);
  const getSearchBarViewAllLabelMessage = useCallback(total => {
    if (isUngatedForUnifiedSearch) {
      return I18n.text('marketplace-ui-assets-core.assets.offeringsSearchBarViewAll', {
        total
      });
    }

    if (offeringTypeIsModule) {
      return I18n.text('marketplace-ui-assets-core.assets.moduleSearchBarViewAll', {
        total
      });
    }

    return I18n.text('marketplace-ui-assets-core.assets.themeSearchBarViewAll', {
      total
    });
  }, [isUngatedForUnifiedSearch, offeringTypeIsModule]);
  const fetcher = useCallback(input => {
    const client = appContext === 'public' ? ecosystemPublicClient : ecosystemAppClient;
    let data;

    if (isUngatedForUnifiedSearch) {
      data = {
        limit,
        search: input,
        offeringType: ['MODULE', 'THEME']
      };
    } else {
      data = [{
        limit,
        search: input
      }];
    }

    const response = client.post(searchUrl, {
      data
    });
    return response.then(res => {
      return isUngatedForUnifiedSearch ? res : res[0];
    }).then(({
      items,
      total
    }) => {
      const options = items.map(item => {
        const {
          companyName,
          name,
          offeringId,
          offeringType: _offeringType,
          slug,
          thumbnailUrl
        } = item;
        const offering = _offeringType === 'MODULE' ? 'module' : 'theme';
        return {
          dropdownText: /*#__PURE__*/_jsx(AssetSearchBarResult, {
            companyName: companyName,
            isUngatedForUnifiedSearch: isUngatedForUnifiedSearch,
            name: name,
            offeringType: _offeringType,
            thumbnailUrl: thumbnailUrl
          }),
          href: getAssetDetailsUrl(_offeringType, slug),
          item,
          linkUse: 'unstyled',
          text: name,
          value: `${offering}::${offeringId}`,
          slug
        };
      });

      if (total > limit) {
        options.push({
          href: getViewAllUrl(input),
          text: getSearchBarViewAllLabelMessage(total),
          value: VIEW_ALL_ASSETS,
          slug: getViewAllSlug(input)
        });
      }

      return options;
    }).then(options => grouped ? [{
      text: searchBarGroupLabelMessage,
      options
    }] : options);
  }, [appContext, isUngatedForUnifiedSearch, getAssetDetailsUrl, getSearchBarViewAllLabelMessage, getViewAllSlug, getViewAllUrl, grouped, limit, searchBarGroupLabelMessage, searchUrl, VIEW_ALL_ASSETS]);
  return fetcher;
}